import { Injectable } from '@angular/core';
import { IOrder } from './models/order.model';
import { IProductSession } from '@core/product/models/product-session.model';
import { ProductHelperService } from '@core/product/product-helper.service';
import { IOrderItem } from '@core/order/models/order-item.model';
import { INumberIncrementProductItem } from '@core/form/number-increment/number-increment-product-item.model';
import { IProductPackageOptionSet } from '../product/models/product-option-set.model';
import { IOptionSetSelection } from '../checkout/model/checkout-option-set-selection.model';
import { DateFormatService } from '@roller/rdl/utilities/date';

@Injectable({
  providedIn: 'root'
})
export class OrderProductOptionSetService {
  constructor(
    private productHelperService: ProductHelperService,
    private dateFormatService: DateFormatService
  ) {}

  onOrderItemSelection(
    order: IOrder,
    ev: INumberIncrementProductItem,
    productOptionSet: IProductPackageOptionSet,
    date: Date,
    session?: IProductSession
  ): undefined | { item: IOrderItem | undefined; isAddition: boolean } {
    const optionSetOrderItem = order.items.find(
      (item) =>
        item.productType === 'package' &&
        item.productId === productOptionSet.packageProductId &&
        (session
          ? this.productHelperService.isSameSession(item.session, session)
          : this.dateFormatService.isSameDay(item.bookingDate, date))
    );
    if (!optionSetOrderItem) {
      return;
    }

    // Get the matching selection for the option set selection index
    const existingSelectionIndex = optionSetOrderItem.optionSetSelections?.findIndex(
      (selection: IOptionSetSelection) => {
        return (
          selection.productId === ev.id &&
          selection.productPackageOptionSetId === productOptionSet.productPackageOptionSetId
        );
      }
    );

    const exists = existingSelectionIndex !== -1;
    const isAddition = !exists || optionSetOrderItem.optionSetSelections[existingSelectionIndex].quantity < ev.quantity;

    if (exists && ev.quantity === 0) {
      optionSetOrderItem.optionSetSelections.splice(existingSelectionIndex, 1);
    } else {
      const selection: IOptionSetSelection = {
        productPackageOptionSetId: productOptionSet.productPackageOptionSetId,
        productId: ev.product.id,
        quantity: ev.quantity,
        productName: ev.product.name,
        productPackageOptionSetName: productOptionSet.name
      };

      exists
        ? (optionSetOrderItem.optionSetSelections[existingSelectionIndex] = selection)
        : optionSetOrderItem.optionSetSelections.push(selection);
    }

    return { item: optionSetOrderItem, isAddition };
  }

  /**
   * This handles the situations where the guest hasn't met the package option set selection requirements, but the item is already in cart.
   * In this case, we need to update the existing selections to meet the required amounts
   */
  setRequiredSelection(optionSets: IProductPackageOptionSet[], orderItem: IOrderItem): IOrderItem {
    optionSets?.forEach((optionSet: IProductPackageOptionSet) => {
      // Get the selections for the option set
      const basketSelectionVariations = orderItem?.optionSetSelections?.filter((selection: IOptionSetSelection) => {
        return selection.productPackageOptionSetId === optionSet.productPackageOptionSetId;
      });

      // Current required selection amount is up to date on the option set
      const remainingRequiredSelectionAmount = optionSet.requiredSelectionAmount;

      // We don't need to add items, we only want to remove them if they exceed the required amount
      if (
        !remainingRequiredSelectionAmount ||
        remainingRequiredSelectionAmount >= 0 ||
        !basketSelectionVariations?.length
      ) {
        return;
      }

      // Remove the required number of items starting from the last one added to the cart
      let remainingToRemove = Math.abs(remainingRequiredSelectionAmount);
      basketSelectionVariations.reverse().forEach((selection) => {
        if (remainingToRemove === 0 || selection.quantity === 0) {
          return;
        }

        if (selection.quantity <= remainingToRemove) {
          // If this variation has fewer items than we need to remove, remove all of them
          remainingToRemove -= selection.quantity;
          selection.quantity = 0;
        } else {
          // Otherwise, remove just what we need
          selection.quantity -= remainingToRemove;
          remainingToRemove = 0;
        }
      });
    });

    // Remove any selections with quantity 0 once done
    orderItem.optionSetSelections = orderItem.optionSetSelections.filter((selection) => selection.quantity > 0);

    return orderItem;
  }
}
